// THE FALLBACK TABLE for the sign-in page's brand-panel texts (owner's decision B1, 4.11.2026) — which
// of the operator's four stored texts the panel shows, and when the translation file's own default stands
// instead.
//
// A MODULE OF ITS OWN, with no React and no HTTP in it, for the reason `tenantRoleHolders.ts` is one on
// the console side: this is the decision the feature consists of, and ADR-0002 Layer 2 wants it tested
// without a browser. Node's own runner can import this file (`--experimental-strip-types`); it could not
// import `./signInTexts`, which pulls in `../lib/api` → `../config`, and `config.ts` reads
// `import.meta.env` and so cannot be loaded outside Vite.
//
// The HTTP read and the hook that drives it are `./signInTexts`, which re-exports everything here so a
// component has one import.

/** The four texts in force, each null when the operator has set nothing for it. */
export interface SignInTexts {
  taglineFi: string | null;
  taglineEn: string | null;
  taglineSubFi: string | null;
  taglineSubEn: string | null;
}

/** Nothing overridden — the state the platform ships in, and the one an unreachable read is read as. */
export const NO_SIGN_IN_TEXTS: SignInTexts = {
  taglineFi: null,
  taglineEn: null,
  taglineSubFi: null,
  taglineSubEn: null,
};

/**
 * What the sign-in surface knows about its panel texts right now.
 * - `loading` — the read is in flight. The panel draws a PLACEHOLDER in this state, never the default:
 *   the point of the feature is that the operator's words are what a visitor reads, and a default
 *   rendered now and replaced a moment later is the flash the owner's decision rules out (the same
 *   principle `TenantBrand`'s loading slot already applies to the tenant's name).
 * - `ready`   — the platform answered; `texts` is what is in force.
 * - `error`   — the read failed (offline, 5xx). The defaults stand: a sign-in page with no slogan at all
 *   is worse than one with the shipped slogan, and neither says anything untrue.
 */
export type SignInTextsState =
  | { status: "loading" }
  | { status: "ready"; texts: SignInTexts }
  | { status: "error" };

/** What the brand panel renders: either a placeholder, or the two texts that are in force. */
export type SignInTextResolution =
  | { status: "pending" }
  | { status: "text"; tagline: string; taglineSub: string };

/**
 * Which of the two stored languages an interface language reads.
 *
 * `en` for anything English and `fi` for everything else, matching the console's own `localized` helper
 * and i18next's `fallbackLng: "fi"`: a regional tag (`en-GB`, `fi-FI`) resolves through its base, and a
 * language the product does not have yet gets the Finnish text rather than nothing.
 */
export function signInTextLanguage(language: string | null | undefined): "fi" | "en" {
  return language?.toLowerCase().startsWith("en") ? "en" : "fi";
}

/** A stored text, or null when it is absent or blank — the two spellings of "use the default". */
function stored(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed === "" ? null : trimmed;
}

/**
 * Resolve the panel's two texts.
 *
 * Three inputs and one answer:
 *   · the read's state;
 *   · the interface language, which picks the fi or the en half of what was read;
 *   · the translation file's defaults, which the CALLER resolves through `t` and passes in — this module
 *     must not reach for i18next, or the table could not be tested without one.
 *
 * The rules, in the order they bite:
 *   1. `loading` is PENDING, and nothing else is.
 *   2. A text the operator set is what is shown, in the language being read.
 *   3. Everything else is the translation file's default: a null (nothing set for that language), a value
 *      that is blank after trimming (which the backend already refuses to store, so this is a belt on
 *      braces rather than a rule of its own), and a read that failed outright.
 *
 * The two texts resolve INDEPENDENTLY, which is the case worth stating: an operator who set a Finnish
 * tagline and nothing else gets their tagline over the shipped sub-line, not an all-or-nothing swap. So
 * do the two LANGUAGES — setting the Finnish pair alone leaves an English visitor on the shipped English
 * text, which is the honest answer and not a gap to paper over with the Finnish one.
 */
export function resolveSignInTexts(
  state: SignInTextsState,
  language: string | null | undefined,
  defaults: { tagline: string; taglineSub: string },
): SignInTextResolution {
  if (state.status === "loading") return { status: "pending" };

  const texts = state.status === "ready" ? state.texts : NO_SIGN_IN_TEXTS;
  const english = signInTextLanguage(language) === "en";

  return {
    status: "text",
    tagline: stored(english ? texts.taglineEn : texts.taglineFi) ?? defaults.tagline,
    taglineSub: stored(english ? texts.taglineSubEn : texts.taglineSubFi) ?? defaults.taglineSub,
  };
}
